import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Search, Filter, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const STATUS_COLORS = {
  'Yeni': 'bg-blue-100 text-blue-800',
  'Arandi': 'bg-yellow-100 text-yellow-800',
  'Yonlendirildi': 'bg-green-100 text-green-800',
  'Sonuclandirildi': 'bg-indigo-100 text-indigo-800',
  'Kaybedildi': 'bg-red-100 text-red-800',
};

const STATUSES = ['', 'Yeni', 'Arandi', 'Yonlendirildi', 'Sonuclandirildi', 'Kaybedildi'];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', sehir: '', hizmet: '', search: '' });
  const [loading, setLoading] = useState(true);

  const loadLeads = () => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (filters.status) params.status = filters.status;
    if (filters.sehir) params.sehir = filters.sehir;
    if (filters.hizmet) params.hizmet = filters.hizmet;
    if (filters.search) params.search = filters.search;
    adminAPI.getLeads(params).then(r => {
      setLeads(r.data.leads);
      setTotal(r.data.total);
      setPages(r.data.pages);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadLeads(); }, [page, filters]);

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Leadler</h1>
        <Badge variant="secondary" className="text-sm">{total} kayit</Badge>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Ara (ad, firma, telefon)..."
                value={filters.search}
                onChange={e => { setFilters(f => ({...f, search: e.target.value})); setPage(1); }}
                className="pl-9"
                data-testid="leads-search"
              />
            </div>
            <select
              value={filters.status}
              onChange={e => { setFilters(f => ({...f, status: e.target.value})); setPage(1); }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="admin-leads-filter-status"
            >
              <option value="">Tum Durumlar</option>
              {STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <Input
              placeholder="Sehir filtrele..."
              value={filters.sehir}
              onChange={e => { setFilters(f => ({...f, sehir: e.target.value})); setPage(1); }}
              data-testid="leads-filter-city"
            />
            <Input
              placeholder="Hizmet filtrele..."
              value={filters.hizmet}
              onChange={e => { setFilters(f => ({...f, hizmet: e.target.value})); setPage(1); }}
              data-testid="leads-filter-service"
            />
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[hsl(221,83%,22%)]"></div></div>
          ) : leads.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">Sonuc bulunamadi</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tarih</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Kod</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Ad Soyad</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Firma</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Sehir</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Hizmet</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Durum</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Islem</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-3 px-4 text-muted-foreground tabular-nums whitespace-nowrap">{new Date(lead.created_at).toLocaleDateString('tr-TR')}</td>
                      <td className="py-3 px-4 font-mono text-xs">{lead.public_code}</td>
                      <td className="py-3 px-4 font-medium">{lead.ad_soyad}</td>
                      <td className="py-3 px-4 text-muted-foreground">{lead.sirket_adi || '-'}</td>
                      <td className="py-3 px-4">{lead.sehir}</td>
                      <td className="py-3 px-4">{lead.hizmet}</td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary" className={STATUS_COLORS[lead.status] || ''}>{lead.status}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Link to={`/admin/leads/${lead.id}`}>
                          <Button variant="ghost" size="sm" data-testid={`lead-view-${lead.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">Sayfa {page} / {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
